import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Loader2 } from "lucide-react";
import { ViralCTABar } from "@/components/share/ViralCTABar";
import { useUnifiedStats } from "@/hooks/useUnifiedStats";

interface StatsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  puzzleNumber?: number;
}

export function StatsDialog({ open, onOpenChange, puzzleNumber = 1 }: StatsDialogProps) {
  const stats = useUnifiedStats();
  
  const { 
    totalPlayed, 
    totalWon, 
    currentStreak, 
    maxStreak,
    winPercentage,
    guessDistribution,
    isLoading 
  } = stats;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" aria-describedby="stats-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Statistics
          </DialogTitle>
          <p id="stats-description" className="sr-only">
            View your game statistics including wins, streaks, and guess distribution.
          </p>
        </DialogHeader>
        
        {isLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : totalPlayed === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No games played yet</p>
            <p className="text-sm mt-2">Start playing to track your statistics!</p>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Main Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center space-y-1">
                <div className="text-3xl font-bold text-primary">{totalPlayed}</div>
                <div className="text-sm text-muted-foreground">Played</div>
              </div>
              <div className="text-center space-y-1">
                <div className="text-3xl font-bold text-primary">{winPercentage}%</div>
                <div className="text-sm text-muted-foreground">Win Rate</div>
              </div>
              <div className="text-center space-y-1">
                <div className="text-3xl font-bold text-primary">{currentStreak}</div>
                <div className="text-sm text-muted-foreground">Current Streak</div>
              </div>
              <div className="text-center space-y-1">
                <div className="text-3xl font-bold text-primary">{maxStreak}</div>
                <div className="text-sm text-muted-foreground">Best Streak</div>
              </div>
            </div>

            {/* Guess Distribution */}
            {totalPlayed > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold">Guess Distribution</h4>
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map(attempt => {
                    const count = guessDistribution[attempt] || 0;
                    const percentage = totalPlayed > 0 ? (count / totalPlayed) * 100 : 0;
                    return (
                      <div key={attempt} className="flex items-center gap-3 text-sm">
                        <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">
                          {attempt}
                        </Badge>
                        <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-500 ease-out"
                            style={{ width: `${Math.max(percentage, 2)}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-muted-foreground">{count}</span>
                      </div>
                    );
                  })}
                  {/* Failed attempts row */}
                  {totalPlayed > totalWon && (
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs bg-destructive/10">
                        ✗
                      </Badge>
                      <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                        <div 
                          className="h-full bg-destructive/60 transition-all duration-500 ease-out"
                          style={{ width: `${Math.max(((totalPlayed - totalWon) / totalPlayed) * 100, 2)}%` }}
                        />
                      </div>
                      <span className="w-8 text-right text-muted-foreground">{totalPlayed - totalWon}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-4 bg-muted/50 rounded-lg border border-dashed">
                <p className="text-sm font-medium">No wins yet</p>
                <p className="text-xs text-muted-foreground mt-1.5">
                  You've played {totalPlayed} {totalPlayed === 1 ? 'game' : 'games'}. 
                  Keep trying—your first win is just around the corner! Try using hints if you're stuck.
                </p>
              </div>
            )}

            {/* Share Stats Section */}
            {totalPlayed > 0 && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-semibold mb-3">Share Your Stats</h4>
                <ViralCTABar 
                  shareOptions={{
                    puzzleNumber,
                    totalPlayed,
                    totalWon,
                    currentStreak,
                    maxStreak,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}